"use client";
import Translations from "@translate/components/SignInIntro/EtfOverlapIntro";
import { I18nUtil } from "@utils/i18n";

import type { FC } from "react";
import { AppImage } from "@components/NextImage";
import { IntroBubbleTail } from "./IntroBubbleTail";
import { IntroEdAvatar } from "./IntroEdAvatar";
import { IntroPrimaryAuthCta } from "./IntroPrimaryAuthCta";
import { SIGN_IN_INTRO_ASSETS } from "./introSlidesConfig";
import type { IntroAuthPlatform } from "./resolveIntroAuthPlatform";
import styles from "./ThemePackIntroMotion.module.css";

const I18n = I18nUtil.createScoped(Translations);

interface EtfOverlapIntroProps {
  platform: IntroAuthPlatform;
  onApple?: () => void;
  onGoogle?: () => void;
  onOtherOptions: () => void;
}

/**
 * ETF 重叠分析广告的 Intro（EDG-561 SC-01）。骨架与 `ThemePackIntro` 相同，
 * 差异只在背景图、文案、chip label。Figma：iOS 109896:82092 · Android 109896:82131 · PC 109921:529954。
 * 4 段进度条 / Typing 气泡为装饰，不实现（O-1，与 ThemePackIntro 一致）。
 */
export const EtfOverlapIntro: FC<EtfOverlapIntroProps> = ({
  platform,
  onApple,
  onGoogle,
  onOtherOptions,
}) => (
  <div
    className="relative h-dvh min-h-[640px] w-full overflow-hidden bg-content_brand_primary md:flex"
    data-testid="etf-overlap-intro"
  >
    <AppImage
      src="/assets/images/etf-overlap/intro-bg.webp"
      alt=""
      width={786}
      height={1704}
      className="pointer-events-none absolute inset-0 h-full w-full object-cover md:hidden"
      data-testid="etf-overlap-intro-background"
    />
    <section className="relative z-10 flex h-full w-full flex-col px-6 pt-[calc(var(--safe-area-inset-top,0px)+102px)] md:w-[43.12%] md:justify-center md:gap-10 md:px-[72px] md:pt-0">
      <div className="flex w-full max-w-[508px] flex-col gap-6 md:gap-10">
        <div className="flex flex-col gap-3 md:gap-6">
          <AppImage
            src={SIGN_IN_INTRO_ASSETS.brandLogo}
            alt="ed"
            width={60}
            height={32}
            className="mb-0 h-8 w-[60px] object-contain md:mb-4"
          />
          <h1 className="headline_regular_xl break-words text-content_inverse_primary md:display_s">
            {I18n.t("Your ETFs might be buying the same stocks.")}
          </h1>
          <p className="body_l text-content_inverse_primary md:body_xl">
            {I18n.t(
              "Connect your broker and I'll show you how much your funds overlap. Sign in and it's free.",
            )}
          </p>
        </div>
        <div className="flex w-full max-w-[508px] items-start gap-2.5">
          <div className={`size-12 shrink-0 ${styles.avatar}`}>
            <IntroEdAvatar />
          </div>
          <div
            className={`relative min-w-0 flex-1 rounded-lg bg-content_primary px-4 py-3.5 md:bg-content_secondary ${styles.bubble}`}
          >
            <IntroBubbleTail />
            <p
              className={`body_medium_l text-content_inverse_primary ${styles.bubbleText}`}
            >
              {/* 不带称呼：广告会话登录前拿不到昵称（C-5） */}
              {I18n.t(
                "Hi, I'm Ed. Sign in and I'll open up your funds. No forms first.",
              )}
            </p>
          </div>
        </div>
      </div>
      <IntroPrimaryAuthCta
        variant="etf_overlap"
        chipLabel={I18n.t("overlap check")}
        platform={platform}
        slideIndex={0}
        onApple={onApple}
        onGoogle={onGoogle}
        onOtherOptions={onOtherOptions}
        onGetStarted={onOtherOptions}
      />
    </section>
    <AppImage
      src="/assets/images/etf-overlap/intro-pc.webp"
      alt=""
      width={1600}
      height={1802}
      className="pointer-events-none hidden h-full min-w-0 flex-1 object-cover md:block"
    />
  </div>
);
